import { useState, useSyncExternalStore } from 'react';
import { AssetList } from '@goldcast/api/content';
import { Clip as Recording } from '@goldcast/player-config/src/types/clip-types';
import { ClipPublishedStatus } from '@goldcast/player-config/src/types/asset-types';
import RecordingRow from './RecordingRow';
import useDownloadAsset from '@/hooks/useDownloadAsset';
import useClipNavigate from '@/hooks/useClipNavigate';
import useAnalytics from '@/hooks/useAnalytics';
import { currentClip } from '@/stores/clip';

interface OriginalRecordingRowProps {
  clipId: string;
  title: string;
  recording?: Recording | null;
  isActive: boolean;
  onSelect: () => void;
}

/**
 * The original recording row in the recordings dropdown, wired for download.
 *
 * Download reuses `useDownloadAsset` (the same clip-download flow used across the app). When the
 * original clip isn't in the local store yet (e.g. the user loaded directly into a duplicate),
 * the hook falls back to opening the original with download intent via `navigateToClip`.
 */
export default function OriginalRecordingRow({
  clipId,
  title,
  recording,
  isActive,
  onSelect
}: OriginalRecordingRowProps) {
  const allClips = useSyncExternalStore(currentClip.subscribe, currentClip.getSnapshot);
  const clip =
    recording ?? allClips[clipId] ?? ({ id: clipId, asset_metadata: {}, title, content: {} } as unknown as Recording);
  const isPublished = clip.asset_metadata.publish_status === ClipPublishedStatus.PUBLISHED;

  const { navigateToClip } = useClipNavigate();
  const { trackRecordingDuplicate } = useAnalytics();
  const [isDownloading, setIsDownloading] = useState(false);

  const { onDownloadClip } = useDownloadAsset({ type: 'CLIP', clip: clip as unknown as AssetList }, navigateToClip);

  const handleDownload = () => {
    trackRecordingDuplicate({ action: 'Download', recordingId: clipId });
    setIsDownloading(true);
    onDownloadClip()
      .catch(() => undefined)
      .finally(() => setIsDownloading(false));
  };

  return (
    <RecordingRow
      id={clipId}
      title={title}
      label="Original"
      isOriginal
      isActive={isActive}
      isPublished={isPublished}
      onSelect={onSelect}
      onDownload={handleDownload}
      isDownloading={isDownloading}
    />
  );
}
